import { X } from "@tamagui/lucide-icons";
import { type ReactNode, useContext, useState } from "react";
import Animated, {
  FadeInDown,
  FadeOutDown,
  useAnimatedStyle,
  withTiming,
} from "react-native-reanimated";
import { XStack, YStack, getTokenValue, useMedia } from "tamagui";

import { Button } from "@/components/Button";
import { LayoutContext } from "@/components/LayoutProvider";
import { Heading } from "@/components/Typography";
import { getShadow } from "@/theme/helpers";

interface NudgeAction {
  label: string;
  onPress: () => void;
  dataTrackingId: string;
}
interface NudgeProps {
  title: string;
  description: ReactNode;
  footer?: ReactNode;
  primaryAction: NudgeAction;
  secondaryAction?: NudgeAction;
  dismissTrackingId?: string;
  isVisible?: boolean;
  onDismiss?: () => void;
  isDismissable?: boolean;
}

export default function Nudge({
  title,
  description,
  footer,
  primaryAction,
  secondaryAction,
  dismissTrackingId,
  isVisible: controlledIsVisible,
  onDismiss,
  isDismissable = true,
}: NudgeProps) {
  const isControlled = controlledIsVisible !== undefined;
  const [internalIsVisible, setInternalIsVisible] = useState(true);
  const isVisible = isControlled ? controlledIsVisible : internalIsVisible;
  const { chatInputHeight } = useContext(LayoutContext);
  const { lg } = useMedia();

  const handleDismiss = () => {
    if (!isControlled) {
      setInternalIsVisible(false);
    }
    onDismiss?.();
  };

  const animatedStyle = useAnimatedStyle(
    () => ({
      bottom: withTiming(chatInputHeight + getTokenValue("$300"), {
        duration: 200,
      }),
    }),
    [chatInputHeight],
  );

  if (!isVisible) {
    return null;
  }

  return (
    <Animated.View
      entering={FadeInDown.duration(300)}
      exiting={FadeOutDown.duration(300)}
      style={[
        {
          position: "absolute",
          width: "100%",
          zIndex: 1,
        },
        animatedStyle,
      ]}
    >
      <YStack
        position="relative"
        maxWidth="$constraint"
        alignSelf="center"
        marginHorizontal="$200"
        $lg={{
          marginHorizontal: 0,
        }}
        paddingVertical="$300"
        paddingHorizontal="$200"
        backgroundColor="$fillInput"
        borderRadius="$150"
        {...getShadow()}
      >
        {isDismissable && (
          <Button.Wrapper
            position="absolute"
            top="$300"
            right="$200"
            zIndex={1}
            aria-label="Close"
            onPress={handleDismiss}
            dataTrackingId={dismissTrackingId}
          >
            <X strokeWidth={1.5} size={20} />
          </Button.Wrapper>
        )}
        <XStack
          alignItems="stretch"
          gap="$200"
          flexDirection="column"
          $lg={{
            flexDirection: "row",
            alignItems: "center",
            paddingRight: isDismissable ? "$400" : undefined,
          }}
        >
          <YStack flex={1} justifyContent="center" gap="$100">
            <Heading size="$md">{title}</Heading>
            {description}
            {footer && lg && <YStack marginTop="$100">{footer}</YStack>}
          </YStack>
          <YStack gap="$100" $lg={{ width: "auto", flexShrink: 0 }}>
            <Button
              width="100%"
              mode="primary"
              onPress={primaryAction.onPress}
              dataTrackingId={primaryAction.dataTrackingId}
            >
              {primaryAction.label}
            </Button>
            {secondaryAction && (
              <Button
                width="100%"
                mode="secondary"
                onPress={secondaryAction.onPress}
                dataTrackingId={secondaryAction.dataTrackingId}
              >
                {secondaryAction.label}
              </Button>
            )}
          </YStack>
        </XStack>
        {footer && !lg && <YStack marginTop="$200">{footer}</YStack>}
      </YStack>
    </Animated.View>
  );
}
